import dayjs from 'dayjs';
import {
  UnifiedTransactionPayload,
  UnifiedTransactionType,
} from '../../../../types/unified-transaction';
import { TransactionType } from '../components/types';
import { TransactionFormState } from '../hooks/useTransactionFormState';

/**
 * Maps frontend TransactionType enum + loanType to UnifiedTransactionType
 */
export const mapToUnifiedType = (
  currentTab: TransactionType,
  loanType: string | null,
  creditCardType: string | null
): UnifiedTransactionType => {
  // Handle loan subtypes
  if (currentTab === TransactionType.LOAN && loanType) {
    const loanTypeMap: Record<string, UnifiedTransactionType> = {
      'borrow': UnifiedTransactionType.BORROW,
      'borrow-expense': UnifiedTransactionType.BORROW_EXPENSE,
      'lend': UnifiedTransactionType.LEND,
      'repay': UnifiedTransactionType.REPAY,
      'collect': UnifiedTransactionType.COLLECT,
    };
    return loanTypeMap[loanType] || UnifiedTransactionType.BORROW;
  }

  // Handle credit card types
  if (currentTab === TransactionType.CREDIT_CARD) {
    if (creditCardType === 'convert_installment') {
      return UnifiedTransactionType.INSTALLMENT_CONVERSION;
    }
    // pay_balance and cash_advance are transfers
    return UnifiedTransactionType.TRANSFER;
  }

  // Basic transaction types
  const tabMap: Record<TransactionType, UnifiedTransactionType> = {
    [TransactionType.EXPENSE]: UnifiedTransactionType.EXPENSE,
    [TransactionType.INCOME]: UnifiedTransactionType.INCOME,
    [TransactionType.TRANSFER]: UnifiedTransactionType.TRANSFER,
    [TransactionType.LOAN]: UnifiedTransactionType.BORROW, // fallback
    [TransactionType.CREDIT_CARD]: UnifiedTransactionType.TRANSFER, // fallback
  };

  return tabMap[currentTab] || UnifiedTransactionType.EXPENSE;
};

/**
 * Builds unified transaction payload from form state
 * Used for both create and update operations
 */
export const buildUnifiedPayload = (
  formState: TransactionFormState
): UnifiedTransactionPayload => {
  const {
    currentTab,
    loanType,
    creditCardType,
    amount,
    description,
    datePicker,
    isExcludedFromReport,
    // Expense/Income
    expenseWallet,
    incomeWallet,
    spendingCategory,
    incomeCategory,
    isInstallment,
    numberOfInstallments,
    installmentFee,
    // Transfer
    transferSourceWallet,
    selectDestinationWallet,
    // Loan
    loanWallet,
    repaymentWallet,
    repaymentDate,
    loansOrdering,
    // Lend
    lendWallets,
    lendExpendWallet,
    sourceWallet,
  } = formState;

  const unifiedType = mapToUnifiedType(currentTab, loanType, creditCardType);

  // Base payload with common fields
  const payload: UnifiedTransactionPayload = {
    type: unifiedType,
    amount: Math.abs(amount || 0),
    description: description || undefined,
    transactionDate: dayjs(datePicker).toISOString(),
    isExcludedFromReport: isExcludedFromReport || false,
  };

  // Add type-specific fields based on unified type
  switch (unifiedType) {
    case UnifiedTransactionType.EXPENSE:
      payload.walletId = expenseWallet?.wallet?.id;
      payload.categoryId = spendingCategory?.id;
      if (isInstallment && numberOfInstallments) {
        payload.isInstallment = true;
        payload.installment = {
          numberOfInstallments: numberOfInstallments,
          installmentFee: installmentFee || 0,
        };
      }
      break;

    case UnifiedTransactionType.INCOME:
      payload.walletId = incomeWallet?.wallet?.id;
      payload.categoryId = incomeCategory?.id;
      break;

    case UnifiedTransactionType.TRANSFER:
      payload.sourceWalletId = transferSourceWallet?.wallet?.id;
      payload.destinationWalletId = selectDestinationWallet?.wallet?.id;
      break;

    case UnifiedTransactionType.BORROW:
      payload.loanWalletId = loanWallet?.wallet?.id;
      payload.walletId = repaymentWallet?.wallet?.id;
      if (repaymentDate) {
        payload.endDate = dayjs(repaymentDate).toISOString();
      }
      break;

    case UnifiedTransactionType.BORROW_EXPENSE:
      // For borrow-expense, the lender wallet is stored in expenseWallet by the form
      payload.loanWalletId = expenseWallet?.wallet?.id;
      payload.categoryId = spendingCategory?.id;
      if (repaymentDate) {
        payload.endDate = dayjs(repaymentDate).toISOString();
      }
      break;

    case UnifiedTransactionType.LEND:
      payload.walletId = sourceWallet?.wallet?.id;
      if (lendWallets && lendWallets.length > 0) {
        payload.lendingWallets = lendWallets.map((w) => ({
          walletId: w.wallet.wallet.id,
          amount: w.allocateAmount || 0,
        }));
        // principalAmount should be sum of lending wallets only (excluding owner's expense)
        payload.amount = lendWallets.reduce((sum, w) => sum + (w.allocateAmount || 0), 0);
      }
      if (lendExpendWallet?.wallet?.wallet?.id) {
        payload.expendWallet = {
          walletId: lendExpendWallet.wallet.wallet.id,
          amount: lendExpendWallet.allocateAmount || 0,
          categoryId: spendingCategory?.id || '',
        };
      }
      break;

    case UnifiedTransactionType.REPAY:
      payload.loanWalletId = loanWallet?.wallet?.id;
      payload.walletId = repaymentWallet?.wallet?.id;
      if (loansOrdering && loansOrdering.length > 0) {
        payload.loanRepaymentAllocation = loansOrdering.map((lo) => ({
          loanId: lo.loanId,
          orderIndex: lo.orderIndex,
        }));
      }
      break;

    case UnifiedTransactionType.COLLECT:
      payload.loanWalletId = loanWallet?.wallet?.id;
      payload.walletId = repaymentWallet?.wallet?.id;
      if (loansOrdering && loansOrdering.length > 0) {
        payload.loanCollectingAllocation = loansOrdering.map((lo) => ({
          loanId: lo.loanId,
          orderIndex: lo.orderIndex,
        }));
      }
      break;

    case UnifiedTransactionType.INSTALLMENT_CONVERSION:
      payload.creditWalletId = selectDestinationWallet?.wallet?.id;
      payload.numberOfInstallments = numberOfInstallments && numberOfInstallments > 0
        ? numberOfInstallments
        : undefined;
      break;
  }

  return payload;
};

/**
 * Checks if the current form state represents a credit card installment conversion
 * This is a special case that uses a different API endpoint
 */
export const isCreditCardInstallmentConversion = (
  currentTab: TransactionType,
  creditCardType: string | null
): boolean => {
  return currentTab === TransactionType.CREDIT_CARD && creditCardType === 'convert_installment';
};
